import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { contactAPI, adminAPI } from '../services/api';
import {
  LayoutDashboard,
  FileText,
  Briefcase,
  Users,
  Settings,
  DollarSign,
  HelpCircle,
  LogOut,
  Menu,
  X,
  Wrench,
  Mail,
  Award,
  BookOpen,
  Search,
  Bell,
  ChevronDown,
  Shield,
  BarChart3,
  Layers,
  Globe,
  MessageSquare,
  UserCog,
  BriefcaseBusiness,
  Activity
} from 'lucide-react';
import { Avatar, Badge } from '../components/ui';

interface ContactMessage {
  _id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: string;
  createdAt: string;
}

interface ActivityItem {
  _id: string;
  action: string;
  description: string;
  createdAt: string;
}

interface SidebarStats {
  pendingMessages: number;
  activeJobs: number;
}

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [sidebarStats, setSidebarStats] = useState<SidebarStats>({ pendingMessages: 0, activeJobs: 0 });
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    fetchNotifications();
    fetchSidebarStats();
  }, []);

  const fetchNotifications = async () => {
    try {
      const [messagesRes, activityRes] = await Promise.all([
        contactAPI.getAll({ limit: 5 }),
        adminAPI.getActivity({ limit: 5 })
      ]);
      setMessages(messagesRes.data.data || []);
      setActivities(activityRes.data.data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchSidebarStats = async () => {
    try {
      const statsRes = await adminAPI.getStats();
      const data = statsRes.data.data;
      setSidebarStats({
        pendingMessages: messages.filter(m => m.status === 'unread').length,
        activeJobs: data?.overview?.activeJobs || 0
      });
    } catch (error) {
      console.error('Error fetching sidebar stats:', error);
    }
  };

  const unreadCount = messages.filter(m => m.status === 'unread').length;

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', description: 'Overview & Stats' },
    { path: '/admin/users', icon: Users, label: 'Users', description: 'Manage Users' },
    { path: '/admin/jobs', icon: BriefcaseBusiness, label: 'Jobs', description: 'Job Listings' },
    { path: '/admin/blogs', icon: FileText, label: 'Blogs', description: 'Blog Posts' },
    { path: '/admin/articles', icon: BookOpen, label: 'Articles', description: 'Articles & News' },
    { path: '/admin/opportunities', icon: Award, label: 'Opportunities', description: 'Scholarships & Internships' },
    { path: '/admin/services', icon: Wrench, label: 'Services', description: 'Service Requests' },
    { path: '/admin/pricing', icon: DollarSign, label: 'Pricing', description: 'Plans & Pricing' },
    { path: '/admin/faqs', icon: HelpCircle, label: 'FAQs', description: 'FAQ Management' },
    { path: '/admin/contact', icon: Mail, label: 'Messages', description: 'Contact Inquiries' },
    { path: '/admin/settings', icon: Settings, label: 'Settings', description: 'System Settings' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getPageTitle = () => {
    const current = menuItems.find(item => isActive(item.path));
    return current?.label || 'Dashboard';
  };

  return (
    <div className="flex h-screen bg-secondary-50">
      {/* Sidebar */}
      <aside 
        className={`
          ${sidebarOpen ? 'w-72' : 'w-20'} 
          bg-gradient-to-b from-secondary-900 to-secondary-800 
          text-white transition-all duration-300 flex flex-col shadow-xl
        `}
      >
        {/* Header */}
        <div className="p-4 flex items-center justify-between border-b border-secondary-700/50">
          {sidebarOpen && (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center shadow-lg">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold">BSAF Admin</h1>
                <p className="text-xs text-secondary-400">Management Portal</p>
              </div>
            </div>
          )}
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)} 
            className="p-2 hover:bg-secondary-700 rounded-lg transition-colors"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Quick Stats */}
        {sidebarOpen && (
          <div className="p-4 border-b border-secondary-700/50">
            <div className="bg-secondary-800/50 rounded-xl p-4 border border-secondary-700/50">
              <div className="flex items-center gap-2 text-secondary-300 text-sm mb-3">
                <BarChart3 className="w-4 h-4" />
                <span>Quick Stats</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-secondary-700/50 rounded-lg p-2 text-center">
                  <p className="text-xl font-bold text-warning-400">{unreadCount}</p>
                  <p className="text-xs text-secondary-400">Pending</p>
                </div>
                <div className="bg-secondary-700/50 rounded-lg p-2 text-center">
                  <p className="text-xl font-bold text-success-400">{sidebarStats.activeJobs}</p>
                  <p className="text-xs text-secondary-400">Active Jobs</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
                  ${active 
                    ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg shadow-primary-600/40 border-l-4 border-white' 
                    : 'text-secondary-300 hover:bg-secondary-700/50 hover:text-white border-l-4 border-transparent'
                  }
                `}
                title={!sidebarOpen ? item.label : undefined}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-white' : 'text-secondary-400'}`} />
                {sidebarOpen && (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{item.label}</p>
                    <p className={`text-xs truncate ${active ? 'text-primary-100' : 'text-secondary-500'}`}>{item.description}</p>
                  </div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-secondary-700/50">
          {sidebarOpen ? (
            <div className="flex items-center gap-3 mb-3 p-2 bg-secondary-800/50 rounded-lg">
              <Avatar name={`${user?.firstName} ${user?.lastName}`} size="md" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.firstName} {user?.lastName}</p>
                <p className="text-xs text-secondary-400 truncate">{user?.email}</p>
              </div>
            </div>
          ) : (
            <div className="flex justify-center mb-3">
              <Avatar name={`${user?.firstName} ${user?.lastName}`} size="md" />
            </div>
          )}
          <button
            onClick={handleLogout}
            className={`
              flex items-center gap-3 px-3 py-2 w-full rounded-lg transition-colors
              text-secondary-300 hover:bg-danger-600/20 hover:text-danger-400
              ${!sidebarOpen ? 'justify-center' : ''}
            `}
            title={!sidebarOpen ? 'Logout' : undefined}
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="text-sm">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="bg-white shadow-sm border-b border-secondary-100 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold text-secondary-900">
                {getPageTitle()}
              </h2>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 bg-secondary-50 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white w-64 transition-all"
                />
              </div>

              {/* Notifications */}
              <div className="relative">
                <button 
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  className="relative p-2 text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 rounded-lg transition-colors"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-danger-500 text-white text-xs font-medium rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                {notificationsOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setNotificationsOpen(false)} 
                    />
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-secondary-100 z-50 overflow-hidden">
                      <div className="p-4 border-b border-secondary-100 flex items-center justify-between">
                        <h3 className="font-semibold text-secondary-900">Notifications</h3>
                        <button className="text-sm text-primary-600 hover:text-primary-700">
                          Mark all read
                        </button>
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {messages.length > 0 ? (
                          messages.slice(0, 5).map((message) => (
                            <div 
                              key={message._id}
                              className={`p-4 border-b border-secondary-50 hover:bg-secondary-50 transition-colors ${message.status === 'unread' ? 'bg-primary-50/50' : ''}`}
                            >
                              <div className="flex gap-3">
                                <div className="w-8 h-8 rounded-full bg-info-100 flex items-center justify-center">
                                  <Mail className="w-4 h-4 text-info-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-secondary-900">{message.name}</p>
                                  <p className="text-xs text-secondary-500 mt-0.5 truncate">{message.subject}</p>
                                  <p className="text-xs text-secondary-400 mt-1">{new Date(message.createdAt).toLocaleDateString()}</p>
                                </div>
                                {message.status === 'unread' && (
                                  <div className="w-2 h-2 bg-primary-500 rounded-full mt-1" />
                                )}
                              </div>
                            </div>
                          ))
                        ) : activities.length > 0 ? (
                          activities.slice(0, 5).map((activity) => (
                            <div 
                              key={activity._id}
                              className="p-4 border-b border-secondary-50 hover:bg-secondary-50 transition-colors"
                            >
                              <div className="flex gap-3">
                                <div className="w-8 h-8 rounded-full bg-success-100 flex items-center justify-center">
                                  <Shield className="w-4 h-4 text-success-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-secondary-900">{activity.action}</p>
                                  <p className="text-xs text-secondary-500 mt-0.5 truncate">{activity.description}</p>
                                  <p className="text-xs text-secondary-400 mt-1">{new Date(activity.createdAt).toLocaleDateString()}</p>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-8 text-center text-secondary-500">
                            No notifications
                          </div>
                        )}
                      </div>
                      <div className="p-3 bg-secondary-50">
                        <Link to="/admin/contact" className="w-full text-center text-sm text-primary-600 hover:text-primary-700 font-medium block">
                          View All Messages
                        </Link>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* User Menu */}
              <div className="relative">
                <button 
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 p-1.5 hover:bg-secondary-100 rounded-lg transition-colors"
                >
                  <Avatar name={`${user?.firstName} ${user?.lastName}`} size="sm" />
                  <ChevronDown className={`w-4 h-4 text-secondary-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {userMenuOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setUserMenuOpen(false)} 
                    />
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-secondary-100 z-50 py-2">
                      <div className="px-4 py-2 border-b border-secondary-100">
                        <p className="text-sm font-medium text-secondary-900">{user?.firstName} {user?.lastName}</p>
                        <p className="text-xs text-secondary-500">{user?.email}</p>
                      </div>
                      <Link 
                        to="/admin/settings"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50"
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </Link>
                      <button 
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-danger-600 hover:bg-danger-50"
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-secondary-50">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
